import { Box, Drawer, DrawerProps, Stack, styled, useTheme } from "@mui/material";
import * as React from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  GetLeftPanelInfos,
  GetRightPanelInfos,
  SetLeftPanelActiveId,
  SetLeftPanelOpen,
  SetRightPanelActiveId,
  SetRightPanelOpen,
} from "../../../Redux/Reducers/redux-slice-layout";
import { useDispatch } from "react-redux";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useLocation } from "react-router";

export interface PanelProps extends DrawerProps {
  width?: number | string;
  withContainer?: boolean;
  header?: React.ReactNode;
  footerContent?: React.ReactNode;
  open?: boolean;
  position?: "left" | "right";
  reload?: any;
  noWrapper?: boolean;
}

const Panel: React.FC<PanelProps> = ({
  header,
  footerContent,
  withContainer = false,
  children,
  open = false,
  position = "right",
  reload,
  noWrapper = false,
  onClose,
  ...otherProps
}) => {
  const panelRef = React.useRef<Element>(undefined);
  const dispatch = useDispatch();
  const rightPanel = useSelector(GetRightPanelInfos);
  const leftPanel = useSelector(GetLeftPanelInfos);
  const theme = useTheme();
  const location = useLocation();

  React.useEffect(() => {
    panelRef.current = document.getElementsByClassName(
      position === "right" ? GlobalConst.RIGHT_PANEL_CLASSNAME : GlobalConst.LEFT_PANEL_CLASSNAME
    )[0];

    if (position === "right" && onClose) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      if (onClose) onClose(e, "escapeKeyDown");
    }
  };

  const body = React.useMemo(
    () =>
      noWrapper ? (
        <Box className={`panel-${position}-wrapper-element`} {...otherProps}>
          {header}
          {children}
          {footerContent && (
            <Stack
              sx={{ background: theme.palette.background.default }}
              bottom={0}
              position={"sticky"}
              direction="row"
              alignItems="center"
              spacing={1.5}
            >
              {footerContent}
            </Stack>
          )}
        </Box>
      ) : (
        <PanelWrapper
          sx={{ overflow: "scroll", overflowX: "hidden" }}
          className={`panel-${position}-wrapper-element`}
          {...otherProps}
        >
          {header}
          <PanelBody>{children}</PanelBody>
          {footerContent && (
            <PanelFooter direction="row" alignItems="center" spacing={1.5}>
              {footerContent}
            </PanelFooter>
          )}
        </PanelWrapper>
      ),
    [header, footerContent, otherProps, children, reload, noWrapper]
  );

  React.useEffect(() => {
    dispatch(
      position === "right" ? SetRightPanelActiveId(open ? reload : null) : SetLeftPanelActiveId(open ? reload : null)
    );

    return () => {
      dispatch(position === "right" ? SetRightPanelActiveId(null) : SetLeftPanelActiveId(null));
    };
  }, [reload, position, open]);

  const render = React.useMemo(
    () => (panelRef.current && body && open ? createPortal(body, panelRef.current) : <></>),
    [body, reload, panelRef.current, open]
  );

  React.useEffect(() => {
    position === "right" ? dispatch(SetRightPanelOpen(open)) : dispatch(SetLeftPanelOpen(open));
    return () => {
      position === "right" ? dispatch(SetRightPanelOpen(false)) : dispatch(SetLeftPanelOpen(false));
    };
  }, [open, reload]);

  if (!body || !panelRef.current || !open) return <></>;
  if (position === "right" && !rightPanel.open) return <></>;
  if (position === "left" && !leftPanel.open) return <></>;

  return render;
};

export default Panel;

const PanelWrapper = styled(Stack)(({ theme }) => ({
  backgroundImage: "none",
  //   height: "100vh",
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  //   overflowY: "scroll",
  ...theme.helpers(theme).scrollBar,
}));

const PanelFooter = styled(Stack)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  zIndex: 1,
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: theme.palette.background.default,
}));

const PanelBody = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  //height: "100%",
  flex: 1,
}));
